import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import { getAvatarColors } from '../utils/avatars';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const ROLES = ['admin', 'manager', 'mr', 'bd', 'scientist', 'rnd_head', 'principal_scientist', 'sales', 'finance', 'procurement', 'software', 'director', 'ceo'];
const DEPARTMENTS = ['Research & Development', 'Quality Control', 'Quality Assurance', 'Regulatory Affairs',
  'Sales & Marketing', 'Business Development', 'Finance', 'Human Resources', 'Operations', 'IT', 'Administration', 'Logistics'];
const ROLE_LABELS = {
  admin: 'Admin', manager: 'Manager', mr: 'MR', bd: 'BD', scientist: 'Scientist',
  rnd_head: 'R&D Head', principal_scientist: 'Principal Scientist',
  sales: 'Sales', finance: 'Finance', procurement: 'Procurement',
  software: 'Software', director: 'Director', ceo: 'CEO',
};
const roleColors = {
  admin: 'error', manager: 'warning', mr: 'info', bd: 'success', scientist: 'primary',
  rnd_head: 'primary', principal_scientist: 'primary', sales: 'success',
  finance: 'warning', procurement: 'info', software: 'info', director: 'error', ceo: 'error',
};
const roleLabel = (r) => ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : '—');
const roleEmoji = {
  admin: '🔑', manager: '👔', mr: '🚗', bd: '💼', scientist: '🔬',
  rnd_head: '🧪', principal_scientist: '🔬', sales: '📈',
  finance: '💰', procurement: '📦', software: '💻', director: '🏛️', ceo: '👑',
};

const defaultForm = { employee_id: '', name: '', email: '', password: '', role: 'mr', territory: '', phone: '', username: '', department: '', reports_to: '', allowed_pages: null };
const defaultPersonaForm = { name: '', description: '', role: 'mr', department: '', territory: '', allowed_pages: null };

// ─── Page definitions for access control ────────────────────────────────────
const PAGE_SECTIONS = [
  {
    section: 'Commercial & Sales',
    pages: [
      { key: 'clients', label: 'Clients', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'partners', label: 'Partners', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'customers', label: 'Customers', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'catalog', label: 'Catalog', defaultRoles: null },
      { key: 'inquiries', label: 'Inquiries', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'quotes', label: 'Quotes', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'orders', label: 'Orders', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'visits', label: 'Visits', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'activities', label: 'Activities', defaultRoles: ['admin', 'manager', 'mr', 'bd'] },
      { key: 'samples', label: 'Samples', defaultRoles: ['admin', 'manager', 'mr'] },
      { key: 'competitors', label: 'Competitors', defaultRoles: ['admin', 'manager', 'mr'] },
    ],
  },
  {
    section: 'Projects & Science',
    pages: [
      { key: 'projects', label: 'Projects', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'coa', label: 'COA', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'chem-structure', label: 'Chem Structure', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'service-requests', label: 'Service Requests', defaultRoles: null },
    ],
  },
  {
    section: 'Finance & Documents',
    pages: [
      { key: 'invoices', label: 'Invoices', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'proforma-invoices', label: 'Proforma Invoices', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'purchase-orders', label: 'Purchase Orders', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'accounting', label: 'Accounting', defaultRoles: ['admin', 'manager'] },
      { key: 'receivables', label: 'Receivables', defaultRoles: ['admin', 'manager'] },
      { key: 'balance-sheet', label: 'Balance Sheet', defaultRoles: ['admin', 'manager'] },
      { key: 'gst-report', label: 'GST Report', defaultRoles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Ops & HR',
    pages: [
      { key: 'attendance', label: 'Attendance', defaultRoles: null },
      { key: 'hr', label: 'HR', defaultRoles: ['admin', 'manager'] },
      { key: 'analytics', label: 'Analytics', defaultRoles: ['admin', 'manager'] },
      { key: 'license-expiry', label: 'License Expiry', defaultRoles: ['admin', 'manager', 'mr'] },
      { key: 'tva', label: 'TVA (Alerts)', defaultRoles: ['admin', 'manager', 'mr', 'bd'] },
      { key: 'capa', label: 'CAPA', defaultRoles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Administration',
    pages: [
      { key: 'users', label: 'User Management', defaultRoles: ['admin', 'manager'] },
      { key: 'territories', label: 'Territories', defaultRoles: ['admin', 'manager'] },
      { key: 'web-inquiries', label: 'Web Inquiries', defaultRoles: ['admin', 'manager'] },
      { key: 'audit-logs', label: 'Audit Logs', defaultRoles: ['admin', 'manager'] },
      { key: 'data-export', label: 'Data Export', defaultRoles: ['admin', 'manager'] },
      { key: 'email-templates', label: 'Email Templates', defaultRoles: ['admin', 'manager'] },
      { key: 'company-settings', label: 'Company Settings', defaultRoles: ['admin'] },
      { key: 'webhooks', label: 'Webhooks', defaultRoles: ['admin'] },
    ],
  },
];
const ALL_PAGES = PAGE_SECTIONS.flatMap(s => s.pages);

// Given a role, return the default page keys
const defaultPagesForRole = (role) =>
  ALL_PAGES.filter(p => !p.defaultRoles || p.defaultRoles.includes(role)).map(p => p.key);

// ─── Persona Page Picker (standalone, no targetUser needed) ─────────────────
function PersonaPagePicker({ role, pages, onChange }) {
  const effective = pages !== null ? pages : defaultPagesForRole(role);
  const useCustom = pages !== null;

  const toggle = (key) => {
    onChange(effective.includes(key) ? effective.filter(k => k !== key) : [...effective, key]);
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={useCustom}
            onChange={e => onChange(e.target.checked ? defaultPagesForRole(role) : null)}
            style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
          />
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Customise page access</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {useCustom ? '(custom override)' : '(using role defaults)'}
          </span>
        </label>
      </div>
      {PAGE_SECTIONS.map(sec => (
        <div key={sec.section} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 6 }}>{sec.section}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 5 }}>
            {sec.pages.map(p => {
              const isDefault = !p.defaultRoles || p.defaultRoles.includes(role);
              const checked = useCustom ? effective.includes(p.key) : isDefault;
              return (
                <label key={p.key} style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '6px 9px',
                  borderRadius: 7, border: '1px solid var(--border)',
                  background: checked ? 'var(--primary-lighter)' : 'var(--surface-2)',
                  cursor: useCustom ? 'pointer' : 'default', userSelect: 'none',
                }}>
                  <input type="checkbox" checked={checked} disabled={!useCustom}
                    onChange={() => toggle(p.key)}
                    style={{ width: 13, height: 13, accentColor: 'var(--primary)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.8rem', fontWeight: checked ? 600 : 400, color: checked ? 'var(--primary)' : 'var(--text-secondary)' }}>
                    {p.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Access Control Panel ────────────────────────────────────────────────────
function AccessControlPanel({ targetUser, onSaved, readOnly }) {
  const [pages, setPages] = useState(null); // null = use role defaults
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    if (!targetUser) return;
    const ap = targetUser.allowed_pages;
    if (ap && Array.isArray(ap) && ap.length > 0) {
      setPages(ap);
      setUseCustom(true);
    } else {
      setPages(defaultPagesForRole(targetUser.role));
      setUseCustom(false);
    }
    setMsg('');
  }, [targetUser]);

  const toggle = (key) => {
    setPages(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      await api.put(`/users/${targetUser.id}`, {
        allowed_pages: useCustom ? pages : null,
        department: targetUser.department, // required by manager-scoped update
      });
      setMsg('Access saved.');
      onSaved && onSaved();
    } catch (err) {
      setMsg(err?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (!targetUser || !pages) return null;

  const effectivePages = useCustom ? pages : defaultPagesForRole(targetUser.role);

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: readOnly ? 'default' : 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={useCustom} disabled={readOnly}
            onChange={e => {
              setUseCustom(e.target.checked);
              if (!e.target.checked) setPages(defaultPagesForRole(targetUser.role));
              else setPages(defaultPagesForRole(targetUser.role));
            }}
            style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
          />
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Use custom page access</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {useCustom ? '(overriding role defaults)' : '(using role defaults)'}
          </span>
        </label>
      </div>

      {PAGE_SECTIONS.map(sec => (
        <div key={sec.section} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 8 }}>{sec.section}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 }}>
            {sec.pages.map(p => {
              const isDefault = !p.defaultRoles || p.defaultRoles.includes(targetUser.role);
              const checked = useCustom ? effectivePages.includes(p.key) : isDefault;
              return (
                <label key={p.key} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                  borderRadius: 7, border: '1px solid var(--border)',
                  background: checked ? 'var(--primary-lighter)' : 'var(--surface-2)',
                  cursor: readOnly || !useCustom ? 'default' : 'pointer',
                  userSelect: 'none', transition: 'all 0.12s',
                }}>
                  <input type="checkbox" checked={checked}
                    disabled={readOnly || !useCustom}
                    onChange={() => toggle(p.key)}
                    style={{ width: 14, height: 14, accentColor: 'var(--primary)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.82rem', fontWeight: checked ? 600 : 400, color: checked ? 'var(--primary)' : 'var(--text-secondary)' }}>
                    {p.label}
                  </span>
                  {!useCustom && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                      {isDefault ? 'default' : ''}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Access'}
          </button>
          {msg && <span style={{ fontSize: '0.82rem', color: msg === 'Access saved.' ? '#10b981' : 'var(--error)' }}>{msg}</span>}
        </div>
      )}
    </div>
  );
}

// ─── User Detail Drawer ──────────────────────────────────────────────────────
function UserDrawer({ user: targetUser, currentUser, onClose, onUpdated, allUsers, territories = [] }) {
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!targetUser) return;
    setForm({
      name: targetUser.name || '',
      email: targetUser.email || '',
      password: '',
      role: targetUser.role || 'mr',
      phone: targetUser.phone || '',
      username: targetUser.username || '',
      territory: targetUser.territory || '',
      department: targetUser.department || '',
      reports_to: targetUser.reports_to || '',
    });
    setMsg({ type: '', text: '' });
    setTab('profile');
  }, [targetUser]);

  if (!targetUser) return null;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      // Managers can only change department
      if (isManager && !isAdmin) {
        const { department } = payload;
        await api.put(`/users/${targetUser.id}`, { department, allowed_pages: targetUser.allowed_pages });
      } else {
        await api.put(`/users/${targetUser.id}`, payload);
      }
      setMsg({ type: 'success', text: 'Saved successfully.' });
      onUpdated();
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to save.' });
    } finally { setSaving(false); }
  };

  const [fg] = getAvatarColors(targetUser.name);
  const initials = targetUser.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ width: 540, background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, flexShrink: 0,
            background: `linear-gradient(135deg, ${fg}22, ${fg}44)`, border: `2px solid ${fg}33`,
            color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '1rem',
          }}>{initials}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{targetUser.name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{targetUser.email}</div>
            <span className={`badge badge-${roleColors[targetUser.role] || 'neutral'}`} style={{ textTransform: 'capitalize', marginTop: 4 }}>{roleLabel(targetUser.role)}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: 'var(--text-muted)', fontSize: '1.3rem', lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
          {['profile', 'access'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 22px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1, textTransform: 'capitalize',
            }}>{t === 'access' ? 'Access Control' : 'Profile'}</button>
          ))}
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>
          {!targetUser.is_active && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>⛔ Account Frozen</div>
              <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This account is frozen and cannot be edited. Unfreeze to make changes.</div>
            </div>
          )}
          {tab === 'profile' && (
            <form onSubmit={handleSave}>
              {msg.text && <div className={`alert alert-${msg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>{msg.text}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} disabled={isManager && !isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} disabled={isManager && !isAdmin} />
                </div>
              </div>

              {isAdmin && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                        {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">New Password</label>
                      <input className="form-control" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Leave blank to keep" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Username <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(for login)</span></label>
                      <input className="form-control" placeholder="e.g. rahul.bd" value={form.username || ''} onChange={e => set('username', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Territory</label>
                      <select className="form-control" value={form.territory} onChange={e => set('territory', e.target.value)}>
                        <option value="">— None —</option>
                        {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <select className="form-control" value={form.department} onChange={e => set('department', e.target.value)}>
                        <option value="">— None —</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Reports To</label>
                      <select className="form-control" value={form.reports_to} onChange={e => set('reports_to', e.target.value)}>
                        <option value="">— None —</option>
                        {allUsers.filter(u => u.is_active).map(m => <option key={m.id} value={m.id}>{m.name} ({roleLabel(m.role)})</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Manager can only edit department */}
              {isManager && !isAdmin && (
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select className="form-control" value={form.department} onChange={e => set('department', e.target.value)}>
                    <option value="">— None —</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          )}

          {tab === 'access' && (
            <AccessControlPanel
              targetUser={targetUser}
              onSaved={onUpdated}
              readOnly={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Users Page ──────────────────────────────────────────────────────────
export default function Users() {
  const { user: currentUser, isAdmin } = useAuth();
  const isManager = currentUser?.role === 'manager';

  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'personas'

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null); // selected user

  // Personas
  const [personas, setPersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [personaModal, setPersonaModal] = useState(false);
  const [editPersona, setEditPersona] = useState(null); // null = create
  const [personaForm, setPersonaForm] = useState(defaultPersonaForm);
  const [personaSaving, setPersonaSaving] = useState(false);
  const [personaError, setPersonaError] = useState('');
  const [personaDeleteConfirm, setPersonaDeleteConfirm] = useState(null);
  const [territories, setTerritories] = useState([]);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users', { params: { role: roleFilter || undefined, search: debouncedSearch || undefined } });
      setUsers(data.data || []);
    } finally { setLoading(false); }
  }, [debouncedSearch, roleFilter]);

  useEffect(() => { load(); }, [load]);

  // When drawer user data changes after save, refresh drawer with latest
  const handleUpdated = async () => {
    await load();
    if (drawer) {
      const { data } = await api.get(`/users/${drawer.id}`).catch(() => ({ data: { data: null } }));
      if (data?.data) setDrawer(data.data);
    }
  };

  const handleAddSave = async () => {
    if (!form.employee_id) { setAddError('Please select an employee.'); return; }
    if (!form.role || !form.password) { setAddError('Role and password are required.'); return; }
    setSaving(true); setAddError('');
    try {
      await api.post('/users', form);
      setAddModal(false);
      load();
      // Refresh available employees list
      const { data } = await api.get('/hr/without-user');
      setAvailableEmployees(data.data || []);
    } catch (err) {
      const d = err.response?.data;
      const msg = d?.message || (Array.isArray(d?.errors) ? d.errors.map(e => e.msg).join(', ') : null) || 'Failed to save user.';
      setAddError(msg);
    }
    finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    await api.patch(`/users/${u.id}/toggle-status`); load();
  };

  const handleDelete = async () => {
    const id = deleteConfirm.id;
    await api.delete(`/users/${id}`);
    setDeleteConfirm(null);
    if (drawer?.id === id) setDrawer(null);
    load();
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/users/${id}/unfreeze`); load(); if (drawer?.id === id) setDrawer(null); }
    catch (err) { console.error('Unfreeze failed', err); }
  };

  // ── Persona helpers ──
  const loadPersonas = useCallback(async () => {
    setPersonasLoading(true);
    try {
      const { data } = await api.get('/personas');
      setPersonas(data.data || []);
    } finally { setPersonasLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'personas') loadPersonas(); }, [activeTab, loadPersonas]);
  useEffect(() => { api.get('/territories').then(r => setTerritories(r.data?.data || r.data || [])).catch(() => { }); }, []);

  const openCreatePersona = () => {
    setEditPersona(null);
    setPersonaForm(defaultPersonaForm);
    setPersonaError('');
    setPersonaModal(true);
  };

  const openEditPersona = (p) => {
    setEditPersona(p);
    setPersonaForm({
      name: p.name || '',
      description: p.description || '',
      role: p.role || 'mr',
      department: p.department || '',
      territory: p.territory || '',
      allowed_pages: p.allowed_pages || null,
    });
    setPersonaError('');
    setPersonaModal(true);
  };

  const handlePersonaSave = async () => {
    if (!personaForm.name || !personaForm.role) { setPersonaError('Name and role are required.'); return; }
    setPersonaSaving(true); setPersonaError('');
    try {
      if (editPersona) {
        await api.put(`/personas/${editPersona.id}`, personaForm);
      } else {
        await api.post('/personas', personaForm);
      }
      setPersonaModal(false);
      loadPersonas();
    } catch (err) { setPersonaError(err.response?.data?.message || 'Failed to save.'); }
    finally { setPersonaSaving(false); }
  };

  const handlePersonaDelete = async () => {
    await api.delete(`/personas/${personaDeleteConfirm.id}`);
    setPersonaDeleteConfirm(null);
    loadPersonas();
  };

  const applyPersona = (persona) => {
    if (!persona) { setForm(defaultForm); return; }
    setForm(p => ({
      ...p,
      role: persona.role || p.role,
      department: persona.department || p.department,
      territory: persona.territory || p.territory,
      allowed_pages: persona.allowed_pages || null,
    }));
  };

  const pf = (k, v) => setPersonaForm(p => ({ ...p, [k]: v }));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const managers = users.filter(u => u.is_active); // Show all active users

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">
            {isAdmin ? 'Manage all users, roles, and page access' : 'View and manage your team members'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isAdmin && activeTab === 'personas' && (
            <button className="btn btn-secondary" onClick={openCreatePersona}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              New Persona
            </button>
          )}
          {isAdmin && activeTab === 'users' && (
            <button className="btn btn-primary" onClick={async () => {
              setForm(defaultForm); setAddError(''); setEmpSearch(''); setAddModal(true);
              if (!personas.length) loadPersonas();
              try { const { data } = await api.get('/hr/without-user'); setAvailableEmployees(data.data || []); } catch { }
            }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[{ key: 'users', label: 'Users' }, { key: 'personas', label: 'Persona Templates' }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: activeTab === t.key ? 700 : 500,
            color: activeTab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (<>
        {/* Filters */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ padding: '14px 16px' }}>
            <div className="search-bar">
              <div className="search-input-wrap">
                <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
                <input className="form-control" placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className="form-control form-select" style={{ width: 150 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* User Cards */}
        {loading ? (
          <div className="loading-overlay"><div className="spinner spinner-lg" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {users.length === 0 && (
              <div className="card"><div className="card-body"><div className="empty-state">
                <p>{isManager ? 'No team members found.' : 'No users found.'}</p>
              </div></div></div>
            )}
            {users.sort((a, b) => a.name.localeCompare(b.name)).map(u => {
              const [fg] = getAvatarColors(u.name);
              const initials = u.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
              const hasCustomAccess = u.allowed_pages && Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0;
              return (
                <div key={u.id} className="card" style={{ opacity: u.is_active ? 1 : 0.65 }}>
                  <div className="card-body">
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                        background: `linear-gradient(135deg, ${fg}22, ${fg}44)`,
                        border: `2px solid ${fg}33`, color: fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: '1rem',
                      }}>{initials}</div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{u.name}</span>
                          <span className={`badge badge-${roleColors[u.role] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{roleLabel(u.role)}</span>
                          {!u.is_active && <span className="badge badge-neutral">Frozen</span>}
                          {hasCustomAccess && (
                            <span className="badge badge-info" title="Has custom page access overrides">Custom Access</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{u.email}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {u.department && <span style={{ marginRight: 8 }}>{u.department}</span>}
                          {u.territory && <span>📍 {u.territory}</span>}
                        </div>
                        {u.last_login && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                            Last login: {new Date(u.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setDrawer(u)}>
                        {isAdmin ? 'Edit / Access' : 'Edit Dept / Access'}
                      </button>
                      {isAdmin && (
                        <>
                          {!u.is_active
                            ? <button className="btn btn-success btn-sm" onClick={() => handleUnfreeze(u.id)}>Unfreeze</button>
                            : <>
                              <button className={`btn btn-sm ${u.is_active ? 'btn-ghost' : 'btn-success'}`}
                                style={{ color: u.is_active ? 'var(--warning)' : undefined }}
                                onClick={() => handleToggle(u)}>
                                {u.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(u)}>Freeze</button>
                            </>
                          }
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>)}

      {/* ── Personas Tab ── */}
      {activeTab === 'personas' && (
        personasLoading ? (
          <div className="loading-overlay"><div className="spinner spinner-lg" /></div>
        ) : (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 18 }}>
              Persona templates let you predefine role, department, territory, and page access — apply them when creating new users to speed up onboarding.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {personas.length === 0 && (
                <div className="card"><div className="card-body"><div className="empty-state">
                  <p>No personas yet. {isAdmin && 'Create one to get started.'}</p>
                </div></div></div>
              )}
              {personas.map(p => {
                const pageCount = p.allowed_pages ? (Array.isArray(p.allowed_pages) ? p.allowed_pages.length : 0) : defaultPagesForRole(p.role).length;
                return (
                  <div key={p.id} className="card">
                    <div className="card-body">
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                          background: 'var(--primary-lighter)', border: '2px solid var(--primary-light)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem',
                        }}>{roleEmoji[p.role] || '👤'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name}</div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                            <span className={`badge badge-${roleColors[p.role] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{roleLabel(p.role)}</span>
                            {p.allowed_pages && <span className="badge badge-info">Custom Access</span>}
                          </div>
                          {p.description && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 5 }}>{p.description}</div>
                          )}
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 6, display: 'flex', gap: 12 }}>
                            {p.department && <span>🏢 {p.department}</span>}
                            {p.territory && <span>📍 {p.territory}</span>}
                            <span>🔐 {pageCount} pages</span>
                          </div>
                          {p.created_by_name && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>Created by {p.created_by_name}</div>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEditPersona(p)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setPersonaDeleteConfirm(p)}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      {/* User Drawer (Edit + Access Control) */}
      {drawer && (
        <UserDrawer
          user={drawer}
          currentUser={currentUser}
          allUsers={users}
          territories={territories}
          onClose={() => setDrawer(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Add User Modal (admin only) */}
      <Modal open={addModal} onClose={() => setAddModal(false)} title="Add User" size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAddModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAddSave} disabled={saving}>{saving ? 'Saving…' : 'Add User'}</button>
        </>}
      >
        {addError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{addError}</div>}

        {/* ── Step 1: Select Employee ── */}
        <div style={{ marginBottom: 16 }}>
          <label className="form-label" style={{ fontWeight: 700 }}>
            Select Employee *
            <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-muted)' }}>
              Only active employees without a login account are listed
            </span>
          </label>
          {availableEmployees.length === 0 ? (
            <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.84rem', color: '#92400e' }}>
              All active employees already have user accounts, or no employees exist yet.
              <a href="/admin/hr" style={{ marginLeft: 6, color: 'var(--primary)', fontWeight: 600 }}>Add employees in HR →</a>
            </div>
          ) : (
            <>
              <input
                className="form-control"
                placeholder="Search by name, code, designation…"
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
                style={{ marginBottom: 6 }}
              />
              {(() => {
                const q = empSearch.toLowerCase();
                const filtered = availableEmployees.filter(e =>
                  !q || `${e.first_name} ${e.last_name}`.toLowerCase().includes(q)
                  || (e.employee_code || '').toLowerCase().includes(q)
                  || (e.designation || '').toLowerCase().includes(q)
                  || (e.department || '').toLowerCase().includes(q)
                );
                return (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: '10px 14px', fontSize: '0.83rem', color: 'var(--text-muted)' }}>No matching employees.</div>
                    ) : filtered.map(emp => {
                      const selected = String(form.employee_id) === String(emp.id);
                      return (
                        <button key={emp.id} type="button"
                          onClick={() => {
                            f('employee_id', emp.id);
                            f('name', `${emp.first_name}${emp.last_name ? ' ' + emp.last_name : ''}`);
                            f('email', emp.email || '');
                            f('phone', emp.phone || '');
                            f('department', emp.department || '');
                          }}
                          style={{
                            width: '100%', textAlign: 'left', padding: '9px 14px',
                            background: selected ? 'var(--primary-lighter)' : 'none',
                            border: 'none', borderBottom: '1px solid var(--border)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                          }}
                          onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--surface-2)'; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'none'; }}
                        >
                          <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: selected ? 'var(--primary)' : 'var(--surface-2)', color: selected ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>
                            {(emp.first_name?.[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: selected ? 'var(--primary)' : 'var(--text-primary)' }}>
                              {emp.first_name} {emp.last_name}
                            </div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 1 }}>
                              {emp.employee_code}{emp.designation ? ` · ${emp.designation}` : ''}{emp.department ? ` · ${emp.department}` : ''}
                            </div>
                          </div>
                          {selected && (
                            <svg width="16" height="16" fill="none" stroke="var(--primary)" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* ── Auto-filled info (read-only) ── */}
        {form.employee_id && (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Auto-filled from employee record</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '0.83rem' }}>
              <div><span style={{ color: 'var(--text-muted)' }}>Name: </span><strong>{form.name}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Email: </span><strong>{form.email || '—'}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Phone: </span><strong>{form.phone || '—'}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Department: </span><strong>{form.department || '—'}</strong></div>
            </div>
          </div>
        )}

        {/* ── Step 2: Login settings ── */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Password *</label>
            <input className="form-control" type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder="Min 8 chars, uppercase, number, special" />
          </div>
          <div className="form-group">
            <label className="form-label">Username <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(optional, for login)</span></label>
            <input className="form-control" placeholder="e.g. rahul.bd" value={form.username || ''} onChange={e => f('username', e.target.value)} />
          </div>
        </div>

        {/* ── Step 3: Role & access ── */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select className="form-control" value={form.role} onChange={e => f('role', e.target.value)}>
              {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Territory</label>
            <select className="form-control" value={form.territory} onChange={e => f('territory', e.target.value)}>
              <option value="">— None —</option>
              {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Reports To</label>
            <select className="form-control" value={form.reports_to} onChange={e => f('reports_to', e.target.value)}>
              <option value="">— None —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({roleLabel(m.role)})</option>)}
            </select>
          </div>
        </div>

        {/* Persona selector */}
        {personas.length > 0 && (
          <div className="form-group" style={{ marginTop: 4, padding: '10px 14px', background: 'var(--primary-lighter)', borderRadius: 8, border: '1px solid var(--primary-light)' }}>
            <label className="form-label" style={{ color: 'var(--primary)', fontWeight: 700, marginBottom: 4 }}>Apply Persona Template</label>
            <select className="form-control" defaultValue=""
              onChange={e => applyPersona(personas.find(p => p.id === +e.target.value) || null)}
            >
              <option value="">— No template —</option>
              {personas.map(p => <option key={p.id} value={p.id}>{roleEmoji[p.role]} {p.name} ({roleLabel(p.role)})</option>)}
            </select>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Pre-fills role, department, and territory</div>
          </div>
        )}
      </Modal>

      {/* Freeze Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze User"
        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}
      >
        <p>Are you sure you want to freeze <strong>{deleteConfirm?.name}</strong>? The account will be locked. You can unfreeze it later.</p>
      </Modal>

      {/* Create / Edit Persona Modal */}
      <Modal open={personaModal} onClose={() => setPersonaModal(false)}
        title={editPersona ? 'Edit Persona' : 'New Persona Template'}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPersonaModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePersonaSave} disabled={personaSaving}>{personaSaving ? 'Saving…' : editPersona ? 'Save Changes' : 'Create Persona'}</button>
        </>}
      >
        {personaError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{personaError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Persona Name *</label>
            <input className="form-control" placeholder="e.g. Field Medical Rep" value={personaForm.name} onChange={e => pf('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select className="form-control" value={personaForm.role} onChange={e => {
              pf('role', e.target.value);
              // reset custom pages when role changes so defaults reload
              if (personaForm.allowed_pages !== null) pf('allowed_pages', defaultPagesForRole(e.target.value));
            }}>
              {ROLES.map(r => <option key={r} value={r}>{roleEmoji[r]} {roleLabel(r)}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Department</label>
            <select className="form-control" value={personaForm.department} onChange={e => pf('department', e.target.value)}>
              <option value="">— None —</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Territory</label>
            <select className="form-control" value={personaForm.territory} onChange={e => pf('territory', e.target.value)}>
              <option value="">— None —</option>
              {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-control" placeholder="Brief description of this persona's responsibilities…" value={personaForm.description} onChange={e => pf('description', e.target.value)} />
        </div>

        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)', marginBottom: 10 }}>Page Access</div>
          <PersonaPagePicker
            role={personaForm.role}
            pages={personaForm.allowed_pages}
            onChange={pages => pf('allowed_pages', pages)}
          />
        </div>
      </Modal>

      {/* Delete Persona Confirm */}
      <Modal open={!!personaDeleteConfirm} onClose={() => setPersonaDeleteConfirm(null)} title="Delete Persona"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPersonaDeleteConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handlePersonaDelete}>Delete</button>
        </>}
      >
        <p>Delete persona <strong>{personaDeleteConfirm?.name}</strong>? This cannot be undone. Existing users are not affected.</p>
      </Modal>
    </div>
  );
}
